export interface DeliveryChallanItem {
  id?: string;
  delivery_challan_id?: string;
  variant_id: string;
  quantity: number;
  unit_price: number;
  taxable_amount?: number;
  tax_rate?: number;
  line_total?: number;
  description?: string;
  hsn_code?: string;
  created_at?: string;
  updated_at?: string;
  variant?: {
    id: string;
    name: string;
    sku: string;
    price: number;
    hsn_code?: string;
    product?: {
      id: string;
      name: string;
    };
  };
}

export enum DeliveryChallanStatus {
  DRAFT = 'draft',
  DISPATCHED = 'dispatched',
  IN_TRANSIT = 'in_transit',
  DELIVERED = 'delivered',
  RETURNED = 'returned'
}

export interface DeliveryChallan {
  id: string;
  challan_number: string;
  sales_order_id: string;
  customer_id: string;
  challan_date: string;
  dispatch_date?: string;
  shipping_address_id: string;
  transporter_name?: string;
  vehicle_number?: string;
  lr_number?: string;
  e_way_bill_number?: string;
  notes?: string;
  status: DeliveryChallanStatus;
  
  // Admin attribution
  created_by_admin_id: string;
  updated_by_admin_id?: string;
  
  // Timestamps
  created_at: string;
  updated_at?: string;
  
  // Relationships
  items: DeliveryChallanItem[];
  customer?: {
    id: string;
    business_name: string;
    contact_person: string;
    email?: string;
    phone?: string;
  };
  sales_order?: {
    id: string;
    order_number: string;
    status: string;
    total_amount: number;
  };
  created_by_admin?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface DeliveryChallanCreate {
  sales_order_id: string;
  customer_id: string;
  challan_date: string;
  dispatch_date?: string;
  shipping_address_id: string;
  transporter_name?: string;
  vehicle_number?: string;
  lr_number?: string;
  e_way_bill_number?: string;
  notes?: string;
  items: Omit<DeliveryChallanItem, 'id' | 'delivery_challan_id' | 'created_at' | 'updated_at' | 'variant'>[];
}

export interface DeliveryChallanUpdate {
  challan_date?: string;
  dispatch_date?: string;
  shipping_address_id?: string;
  transporter_name?: string;
  vehicle_number?: string;
  lr_number?: string;
  e_way_bill_number?: string;
  notes?: string;
  items?: Omit<DeliveryChallanItem, 'id' | 'delivery_challan_id' | 'created_at' | 'updated_at' | 'variant'>[];
}

export interface DeliveryChallanStatusUpdate {
  status: DeliveryChallanStatus;
  notes?: string;
}

export interface DeliveryChallanFromSalesOrderRequest {
  sales_order_id: string;
  challan_date?: string;
  dispatch_date?: string;
  transporter_name?: string;
  vehicle_number?: string;
  lr_number?: string;
  e_way_bill_number?: string;
  notes?: string;
}

export interface DeliveryChallanSearchFilters {
  customer_id?: string;
  sales_order_id?: string;
  status?: DeliveryChallanStatus;
  challan_date_from?: string;
  challan_date_to?: string;
  dispatch_date_from?: string;
  dispatch_date_to?: string;
  search_term?: string;
  created_by_admin_id?: string;
  transporter_name?: string;
}

export interface DeliveryChallanListResponse {
  delivery_challans: DeliveryChallan[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Status color mapping for consistent UI
export const DELIVERY_CHALLAN_STATUS_COLORS: Record<DeliveryChallanStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [DeliveryChallanStatus.DRAFT]: 'default',
  [DeliveryChallanStatus.DISPATCHED]: 'info',
  [DeliveryChallanStatus.IN_TRANSIT]: 'warning',
  [DeliveryChallanStatus.DELIVERED]: 'success',
  [DeliveryChallanStatus.RETURNED]: 'error'
};

// Status labels for display
export const DELIVERY_CHALLAN_STATUS_LABELS: Record<DeliveryChallanStatus, string> = {
  [DeliveryChallanStatus.DRAFT]: 'Draft',
  [DeliveryChallanStatus.DISPATCHED]: 'Dispatched',
  [DeliveryChallanStatus.IN_TRANSIT]: 'In Transit',
  [DeliveryChallanStatus.DELIVERED]: 'Delivered',
  [DeliveryChallanStatus.RETURNED]: 'Returned'
};

export interface DeliveryChallanCreationResponse {
  success: boolean;
  delivery_challan_id: string;
  challan_number: string;
  sales_order_id: string;
  sales_order_number: string;
  message: string;
}